import React, { useRef, useState } from 'react';
import { Upload, FileText, X, Info } from 'lucide-react';
import { readReferralCsv, REFERRAL_CSV_HINT } from '../../services/referralCsv';

/**
 * Admin-only referring-domain CSV picker for the competitor brand.
 *
 * Give it a file and the pipeline uses those domains instead of calling
 * SEMrush; leave it empty and the run falls back to SEMrush exactly as before.
 * Render it only when the signed-in user is the admin — the server enforces the
 * same rule, this just hides a control nobody else can use.
 *
 * Controlled by the parent: it owns { file, rows } so it can clear them after a
 * successful submit and send `rows` as `csvBacklinks`.
 */
const ReferralCsvUpload = ({ file, rows, onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const handleSelect = async (event) => {
    const picked = event.target.files?.[0];
    if (!picked) return;

    setError('');
    const { rows: parsed, error: parseError } = await readReferralCsv(picked);
    if (parseError) {
      setError(parseError);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onChange({ file: picked, rows: parsed });
  };

  const handleClear = () => {
    setError('');
    if (inputRef.current) inputRef.current.value = '';
    onChange({ file: null, rows: [] });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
        Referring domains CSV
        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400">
          optional — admin only
        </span>
      </label>

      {!file ? (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-600 dark:text-slate-400 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            Upload CSV of referring domains
          </button>
          <p className="mt-1.5 flex items-start gap-1 text-xs text-gray-500 dark:text-slate-400">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>No file? We&apos;ll pull the referring domains from SEMrush. {REFERRAL_CSV_HINT}.</span>
          </p>
        </>
      ) : (
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/40">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 shrink-0 text-green-700 dark:text-green-400" />
            <div className="min-w-0">
              <p className="text-sm text-green-900 dark:text-green-200 truncate">{file.name}</p>
              <p className="text-xs text-green-700 dark:text-green-400">
                {rows.length.toLocaleString()} referring domains loaded — SEMrush will be skipped
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="p-1 rounded text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 disabled:opacity-50"
            title="Remove CSV and use SEMrush instead"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={handleSelect}
        className="hidden"
      />

      {error && (
        <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default ReferralCsvUpload;
